import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
} from "react";

import openSocket from "../../../services/socket-io";
import useTickets from "../../../hooks/useTickets";
import { AuthContext } from "../../../context/Auth/AuthContext";

import TicketItem from "./TicketItem";

// Reducer da lista (padrão copiado do TicketsList real): mantém tickets em ordem
// e reage aos eventos de socket "ticket" e "appMessage".
const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;
    newTickets.forEach((ticket) => {
      const idx = state.findIndex((t) => t.id === ticket.id);
      if (idx !== -1) {
        state[idx] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(idx, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });
    return [...state];
  }
  if (action.type === "RESET_UNREAD") {
    const idx = state.findIndex((t) => t.id === action.payload);
    if (idx !== -1) state[idx].unreadMessages = 0;
    return [...state];
  }
  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;
    const idx = state.findIndex((t) => t.id === ticket.id);
    if (idx !== -1) state[idx] = ticket;
    else state.unshift(ticket);
    return [...state];
  }
  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;
    const idx = state.findIndex((t) => t.id === ticket.id);
    if (idx !== -1) {
      state[idx] = ticket;
      state.unshift(state.splice(idx, 1)[0]);
    } else {
      state.unshift(ticket);
    }
    return [...state];
  }
  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const idx = state.findIndex((t) => t.contactId === contact.id);
    if (idx !== -1) state[idx].contact = contact;
    return [...state];
  }
  if (action.type === "DELETE_TICKET") {
    const idx = state.findIndex((t) => t.id === action.payload);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
  return state;
};

// Uma sub-lista para um status (open/pending/closed ou busca). Encapsula
// useTickets + socket e reporta os tickets carregados ao pai via onData.
const useTicketsSubscription = ({
  status,
  searchParam,
  showAll,
  selectedQueueIds,
  selectedWhatsappId,
  blockGroups,
}) => {
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [
    status,
    searchParam,
    showAll,
    selectedQueueIds,
    selectedWhatsappId,
    blockGroups,
  ]);

  const { tickets, hasMore, loading, count } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    queueIds: JSON.stringify(selectedQueueIds),
    whatsappId: selectedWhatsappId || undefined,
    // Filtro de grupos no servidor — count e paginação já vêm sem grupos.
    // O filtro client-side no `list` continua como cinto de segurança.
    blockGroups: blockGroups ? "true" : undefined,
  });

  useEffect(() => {
    if (!status && !searchParam) return;
    dispatch({ type: "LOAD_TICKETS", payload: tickets });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  useEffect(() => {
    const socket = openSocket();

    // Espelha o filtro do servidor nos eventos de socket — sem isto um ticket
    // de outra conexão/fila entraria na lista em tempo real mesmo filtrado.
    const matchesWhatsapp = (ticket) =>
      !selectedWhatsappId || ticket.whatsappId === selectedWhatsappId;

    const shouldUpdate = (ticket) =>
      !searchParam &&
      (!ticket.userId || ticket.userId === user?.id || showAll) &&
      (!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1) &&
      matchesWhatsapp(ticket);

    const notInQueues = (ticket) =>
      (ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1) ||
      !matchesWhatsapp(ticket);

    socket.on("connect", () => {
      if (status) socket.emit("joinTickets", status);
      else socket.emit("joinNotification");
    });

    socket.on("ticket", (data) => {
      if (data.action === "updateUnread") {
        dispatch({ type: "RESET_UNREAD", payload: data.ticketId });
      }
      if (data.action === "update" && shouldUpdate(data.ticket)) {
        dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
      }
      if (data.action === "update" && notInQueues(data.ticket)) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    });

    socket.on("appMessage", (data) => {
      if (data.action === "create" && shouldUpdate(data.ticket)) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        dispatch({ type: "UPDATE_TICKET_CONTACT", payload: data.contact });
      }
    });

    return () => socket.disconnect();
  }, [
    status,
    searchParam,
    showAll,
    user,
    selectedQueueIds,
    selectedWhatsappId,
  ]);

  return {
    ticketsList,
    hasMore,
    loading,
    count,
    loadMore: () => setPageNumber((p) => p + 1),
  };
};

// Botão de aba (underline / segmented / pills), variante-aware.
const TabButton = ({ V, active, onClick, children, style }) => {
  const base = {
    flex: 1,
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: V.tabWeight,
    fontSize: V.tabSize,
    letterSpacing: ".06em",
  };
  if (V.tabStyle === "pills") {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          ...base,
          background: active ? "#fff" : "transparent",
          borderRadius: 999,
          color: active ? V.prim : V.mut,
          padding: "8px 0",
          ...style,
        }}
      >
        {children}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...base,
        borderBottom: `3px solid ${active ? V.prim : "transparent"}`,
        color: active ? V.prim : V.mut,
        padding: "13px 0 10px",
        ...style,
      }}
    >
      {children}
    </button>
  );
};

const CountBadge = ({ color, count }) => (
  <span
    style={{
      background: color,
      color: "#fff",
      borderRadius: 999,
      minWidth: 17,
      height: 17,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 10,
      padding: "0 4px",
      marginLeft: 6,
    }}
  >
    {count}
  </span>
);

// Painel esquerdo (348px). Combina 3 subscrições (open/pending/closed) e busca.
const TicketPanel = ({
  V,
  settings,
  blockGroups,
  tab, // "inbox" | "resolvidos" | "busca"
  onTabChange,
  sub, // "open" | "pending"
  onSubChange,
  search,
  onSearchChange,
  showAll,
  onToggleShowAll,
  queueIds,
  userQueues,
  onQueueChange,
  whatsappId,
  whatsApps,
  onWhatsappChange,
  selectedTicketId,
  leadsByNumber,
  onSelectTicket,
  onAcceptTicket,
  onNewTicket,
}) => {
  const showPrev = settings.densidade === "rico";

  // Subscrições sempre montadas para manter contadores das sub-tabs vivos.
  const openSub = useTicketsSubscription({
    status: "open",
    searchParam: "",
    showAll,
    selectedQueueIds: queueIds,
    selectedWhatsappId: whatsappId,
    blockGroups,
  });
  const pendingSub = useTicketsSubscription({
    status: "pending",
    searchParam: "",
    showAll,
    selectedQueueIds: queueIds,
    selectedWhatsappId: whatsappId,
    blockGroups,
  });
  const closedSub = useTicketsSubscription({
    status: "closed",
    searchParam: "",
    showAll,
    selectedQueueIds: queueIds,
    selectedWhatsappId: whatsappId,
    blockGroups,
  });
  const searchSub = useTicketsSubscription({
    status: "",
    searchParam: tab === "busca" ? search : "",
    showAll: true,
    selectedQueueIds: queueIds,
    selectedWhatsappId: whatsappId,
    blockGroups,
  });

  // Contador real vem do servidor (count do findAndCountAll), não do tamanho
  // da página carregada (que trava em 40 por página). Fallback para o tamanho
  // da lista só enquanto a primeira resposta não chegou.
  const cAtend =
    openSub.count === 0 && openSub.loading
      ? openSub.ticketsList.length
      : openSub.count;
  const cAguard =
    pendingSub.count === 0 && pendingSub.loading
      ? pendingSub.ticketsList.length
      : pendingSub.count;

  const list = useMemo(() => {
    let base;
    if (tab === "busca") base = searchSub.ticketsList;
    else if (tab === "resolvidos") base = closedSub.ticketsList;
    else base = sub === "open" ? openSub.ticketsList : pendingSub.ticketsList;
    if (blockGroups) base = base.filter((t) => t.isGroup !== true);
    // Ordena na renderização pelo horário real da última mensagem (fallback
    // updatedAt para ticket antigo sem a coluna). O reducer vira só cache:
    // update in-place via socket e merge de páginas saem ordenados daqui.
    return [...base].sort(
      (a, b) =>
        new Date(b.lastMessageAt || b.updatedAt) -
        new Date(a.lastMessageAt || a.updatedAt),
    );
  }, [
    tab,
    sub,
    blockGroups,
    searchSub.ticketsList,
    closedSub.ticketsList,
    openSub.ticketsList,
    pendingSub.ticketsList,
  ]);

  const handleScroll = (sy) => (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      if (sy.hasMore && !sy.loading) {
        // Nudge anti-loop (padrão da TicketsList legada): afasta o scroll do
        // fundo antes do loadMore, senão o onScroll re-dispara em cascata.
        e.currentTarget.scrollTop = scrollTop - 100;
        sy.loadMore();
      }
    }
  };

  const activeScroll =
    tab === "busca"
      ? searchSub
      : tab === "resolvidos"
        ? closedSub
        : sub === "open"
          ? openSub
          : pendingSub;

  const funnel = V.key === "b";
  const soft = V.key === "c";

  const controlBtn = {
    fontFamily: "inherit",
    cursor: "pointer",
  };

  const listPadding = soft ? "4px 10px 12px" : "0";

  return (
    <div
      style={{
        width: 348,
        flex: "none",
        background: V.panelBg,
        borderRight: V.panelBorder,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Tabs principais */}
      <div
        style={{
          display: "flex",
          gap: soft ? 5 : 0,
          padding: soft ? "10px 12px 0" : 0,
          borderBottom: soft ? "none" : V.divider,
        }}
      >
        <TabButton
          V={V}
          active={tab === "inbox"}
          onClick={() => onTabChange("inbox")}
        >
          INBOX
        </TabButton>
        <TabButton
          V={V}
          active={tab === "resolvidos"}
          onClick={() => onTabChange("resolvidos")}
        >
          RESOLVIDOS
        </TabButton>
        <TabButton
          V={V}
          active={tab === "busca"}
          onClick={() => onTabChange("busca")}
        >
          BUSCA
        </TabButton>
      </div>

      {/* Linha de controles (inbox/resolvidos) ou input de busca */}
      {tab === "busca" ? (
        <div
          style={{
            padding: "10px 12px",
            borderBottom: soft ? "none" : V.divider,
          }}
        >
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar por nome, telefone ou tag…"
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: V.inputBg,
              border: V.inputBorder,
              borderRadius: V.inputRadius,
              padding: "8px 14px",
              fontSize: 12.5,
              fontFamily: "inherit",
              color: V.textStrong,
              outline: "none",
            }}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: soft ? "10px 12px" : "10px 12px",
            borderBottom: soft ? "none" : V.divider,
            background: funnel ? "#FAFBFC" : "transparent",
          }}
        >
          <button
            type="button"
            onClick={onNewTicket}
            style={{
              ...controlBtn,
              background: funnel ? V.prim : "#fff",
              border: funnel ? "none" : `1.5px solid ${V.prim}`,
              color: funnel ? "#fff" : V.prim,
              borderRadius: V.pillRadius,
              padding: "6px 14px",
              fontWeight: funnel ? 700 : 900,
              fontSize: 11,
              letterSpacing: funnel ? "normal" : ".05em",
            }}
          >
            {funnel ? "+ Novo" : "NOVO"}
          </button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, fontWeight: 800, color: V.sublabel }}>
            Todos
          </span>
          <button
            type="button"
            onClick={onToggleShowAll}
            style={{
              width: 36,
              height: 20,
              borderRadius: 999,
              border: "none",
              background: showAll ? V.prim : V.track,
              cursor: "pointer",
              padding: 2,
              display: "flex",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "#fff",
                marginLeft: showAll ? 16 : 0,
                transition: "margin-left .15s",
              }}
            />
          </button>
          <select
            value={queueIds.length === 1 ? String(queueIds[0]) : ""}
            onChange={(e) => onQueueChange(e.target.value)}
            style={{
              ...controlBtn,
              background: "#fff",
              border: V.inputBorder,
              borderRadius: V.pillRadius,
              padding: "6px 10px",
              fontSize: 11.5,
              fontWeight: 800,
              color: V.sublabel,
            }}
          >
            <option value="">Filas: todas</option>
            {userQueues.map((q) => (
              <option key={q.id} value={String(q.id)}>
                {q.name}
              </option>
            ))}
          </select>
          {(whatsApps || []).length > 1 && (
            <select
              value={whatsappId ? String(whatsappId) : ""}
              onChange={(e) => onWhatsappChange(e.target.value)}
              title="Filtrar tickets por conexão de WhatsApp"
              style={{
                ...controlBtn,
                background: "#fff",
                border: V.inputBorder,
                borderRadius: V.pillRadius,
                padding: "6px 10px",
                fontSize: 11.5,
                fontWeight: 800,
                color: V.sublabel,
                maxWidth: 150,
              }}
            >
              <option value="">Conexões: todas</option>
              {whatsApps.map((w) => (
                <option key={w.id} value={String(w.id)}>
                  {w.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Sub-tabs (só inbox) */}
      {tab === "inbox" &&
        (V.subTabStyle === "segmented" ? (
          <div style={{ padding: "9px 12px", borderBottom: V.divider }}>
            <div
              style={{
                display: "flex",
                background: "#F4F6F9",
                borderRadius: 8,
                padding: 3,
                gap: 3,
              }}
            >
              <button
                type="button"
                onClick={() => onSubChange("open")}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: sub === "open" ? "#fff" : "transparent",
                  border: "none",
                  borderRadius: 6,
                  color: sub === "open" ? V.prim : V.mut,
                  fontWeight: 700,
                  fontSize: 11.5,
                  padding: "6px 0",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  boxShadow:
                    sub === "open" ? "0 1px 2px rgba(20,30,50,.1)" : "none",
                }}
              >
                Atendendo
                <CountBadge color="#2E90FA" count={cAtend} />
              </button>
              <button
                type="button"
                onClick={() => onSubChange("pending")}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: sub === "pending" ? "#fff" : "transparent",
                  border: "none",
                  borderRadius: 6,
                  color: sub === "pending" ? V.prim : V.mut,
                  fontWeight: 700,
                  fontSize: 11.5,
                  padding: "6px 0",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  boxShadow:
                    sub === "pending" ? "0 1px 2px rgba(20,30,50,.1)" : "none",
                }}
              >
                Aguardando
                <CountBadge color="#EC4899" count={cAguard} />
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              gap: soft ? 14 : 0,
              padding: soft ? "2px 16px 8px" : 0,
              borderBottom: soft ? "none" : V.divider,
            }}
          >
            <button
              type="button"
              onClick={() => onSubChange("open")}
              style={{
                flex: soft ? "none" : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "none",
                border: "none",
                borderBottom: `${soft ? 2.5 : 3}px solid ${
                  sub === "open" ? V.prim : "transparent"
                }`,
                color: sub === "open" ? V.prim : V.mut,
                fontWeight: 900,
                fontSize: 11,
                letterSpacing: ".07em",
                padding: soft ? "7px 2px 6px" : "11px 0 9px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              ATENDENDO
              <CountBadge color="#2E90FA" count={cAtend} />
            </button>
            <button
              type="button"
              onClick={() => onSubChange("pending")}
              style={{
                flex: soft ? "none" : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "none",
                border: "none",
                borderBottom: `${soft ? 2.5 : 3}px solid ${
                  sub === "pending" ? V.prim : "transparent"
                }`,
                color: sub === "pending" ? V.prim : V.mut,
                fontWeight: 900,
                fontSize: 11,
                letterSpacing: ".07em",
                padding: soft ? "7px 2px 6px" : "11px 0 9px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              AGUARDANDO
              <CountBadge color="#EC4899" count={cAguard} />
            </button>
          </div>
        ))}

      {/* Lista de tickets */}
      <div
        onScroll={handleScroll(activeScroll)}
        style={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          padding: listPadding,
          display: soft ? "flex" : "block",
          flexDirection: soft ? "column" : undefined,
          gap: soft ? 8 : undefined,
        }}
      >
        {list.length === 0 ? (
          <div
            style={{
              padding: "26px 16px",
              textAlign: "center",
              fontSize: 12,
              fontWeight: 700,
              color: V.labelMuted,
            }}
          >
            Nenhum ticket aqui
          </div>
        ) : (
          list.map((ticket) => (
            <TicketItem
              key={ticket.id}
              V={V}
              ticket={ticket}
              lead={leadsByNumber[ticket.contact?.number]}
              active={ticket.id === selectedTicketId}
              showPrev={showPrev}
              showStatus={tab === "busca"}
              slaLimite={settings.slaLimite}
              onSelect={() => onSelectTicket(ticket)}
              onAccept={() => onAcceptTicket(ticket)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default TicketPanel;
